import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import Toast from "./Toast";

type ToastType = "success" | "error" | "info";

type ToastState = {
  message: string;
  type: ToastType;
  durationMs?: number;
} | null;

type ToastApi = {
  show: (message: string, type?: ToastType, durationMs?: number) => void;
  success: (message: string, durationMs?: number) => void;
  error: (message: string, durationMs?: number) => void;
  info: (message: string, durationMs?: number) => void;
  clear: () => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<ToastState>(null);

  const clear = useCallback(() => setToast(null), []);

  const show = useCallback(
    (message: string, type: ToastType = "info", durationMs = 2000) => {
      setToast({ message, type, durationMs });
    },
    [],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (m, d) => show(m, "success", d),
      error: (m, d) => show(m, "error", d),
      info: (m, d) => show(m, "info", d),
      clear,
    }),
    [show, clear],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toast ? (
        <Toast
          message={toast.message}
          type={toast.type}
          durationMs={toast.durationMs}
          onClose={clear}
        />
      ) : null}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
